import type { AbsenceDayMarker } from "@/lib/employeeAbsences";
import type { EmployeeCalendarDay } from "@/lib/employeeCalendar";
import type { EmployeeTimeYearSummary } from "@/lib/employeeTimes";

export const MONTH_LONG = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function isWeekend(year: number, month: number, day: number): boolean {
  const weekday = new Date(year, month - 1, day).getDay();
  return weekday === 0 || weekday === 6;
}

/** Kompakte Stundenanzeige in Tageszellen (ohne „ h“-Suffix). */
export function formatCompactHours(hours: number): string {
  if (hours <= 0) return "";
  return Number(hours).toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Laufender Überstundenstand wie Excel „Aktuell +/-“ (H:MM, Minus bei negativ). */
export function formatOvertimeBalanceHhmm(hours: number): string {
  if (!Number.isFinite(hours)) return "";
  const negative = hours < -0.0001;
  const totalMinutes = Math.round(Math.abs(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${negative ? "-" : ""}${h}:${String(m).padStart(2, "0")}`;
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** „Donnerstag, 5. Juni 2026“ aus einem ISO-Datum. */
export function formatDayHeading(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const date = new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export interface YearGridCell {
  hours: number;
  heroHours: number;
  /** Laufender Überstundenstand (Aktuell +/-) nach diesem Tag; null wenn unbekannt. */
  overtimeBalanceHours: number | null;
  /** Ausbezahlte Überstunden an diesem Tag (0 wenn keine). */
  payoutHours: number;
  markers: AbsenceDayMarker[];
  heroMarkers: AbsenceDayMarker[];
  calendarDay: EmployeeCalendarDay | null;
  month: number;
  day: number;
  iso: string;
  isWeekend: boolean;
  /** ArbZG-Verstoß ohne Begründung – zählt noch nicht in Summen/Konto. */
  pendingJustification?: boolean;
}

export interface YearMonthGrid {
  month: number;
  total: number;
  appTotal: number;
  heroTotal: number;
  /** Zellen in Wochenraster-Reihenfolge; null = Platzhalter vor/nach dem Monat. */
  cells: Array<YearGridCell | null>;
}

export interface YearGridData {
  months: YearMonthGrid[];
  yearTotal: number;
  yearAppTotal: number;
  yearHeroTotal: number;
  yearPayoutTotal: number;
  workDayCount: number;
  absenceDayCount: number;
}

/** Baut das Wochenraster für einen einzelnen Monat (wie in der Jahresübersicht). */
export function buildMonthGrid(
  year: number,
  month: number,
  hoursByDay: Map<number, number>,
  absenceByDay: Map<string, AbsenceDayMarker[]>,
  calendarByDay: Map<string, EmployeeCalendarDay>,
  heroOverlay?: {
    hoursByDay: Map<string, number>;
    absenceByDay: Map<string, AbsenceDayMarker[]>;
  },
  overtimeBalanceByDay?: Map<string, number> | Record<string, number> | null,
  payoutsByDay?: Map<string, number> | Record<string, number> | null,
  pendingJustificationByDay?: Map<number, boolean> | null,
): YearMonthGrid {
  const balanceMap =
    overtimeBalanceByDay instanceof Map
      ? overtimeBalanceByDay
      : overtimeBalanceByDay
        ? new Map(Object.entries(overtimeBalanceByDay).map(([k, v]) => [k, Number(v)]))
        : null;
  const payoutMap =
    payoutsByDay instanceof Map
      ? payoutsByDay
      : payoutsByDay
        ? new Map(Object.entries(payoutsByDay).map(([k, v]) => [k, Number(v)]))
        : null;
  const numDays = daysInMonth(year, month);
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const cells: Array<YearGridCell | null> = Array.from({ length: firstWeekday }, () => null);
  let monthHeroTotal = 0;
  let appTotal = 0;

  for (let day = 1; day <= numDays; day++) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const hours = hoursByDay.get(day) ?? 0;
    const markers = absenceByDay.get(iso) ?? [];
    const heroHours = heroOverlay?.hoursByDay.get(iso) ?? 0;
    const heroMarkers = heroOverlay?.absenceByDay.get(iso) ?? [];
    const balanceRaw = balanceMap?.get(iso);
    const overtimeBalanceHours =
      balanceRaw != null && Number.isFinite(Number(balanceRaw)) ? Number(balanceRaw) : null;
    const payoutRaw = payoutMap?.get(iso);
    const payoutHours =
      payoutRaw != null && Number.isFinite(Number(payoutRaw)) ? Number(payoutRaw) : 0;
    const pendingJustification = Boolean(pendingJustificationByDay?.get(day));
    // Offene Begründung: Stunden anzeigen, aber nicht in die Monatssumme.
    if (!pendingJustification) {
      appTotal += hours;
    }
    monthHeroTotal += heroHours;
    cells.push({
      hours,
      heroHours,
      overtimeBalanceHours,
      payoutHours,
      markers,
      heroMarkers,
      calendarDay: calendarByDay.get(iso) ?? null,
      month,
      day,
      iso,
      isWeekend: isWeekend(year, month, day),
      pendingJustification,
    });
  }

  while (cells.length % 7 !== 0) cells.push(null);

  return {
    month,
    total: appTotal + monthHeroTotal,
    appTotal,
    heroTotal: monthHeroTotal,
    cells,
  };
}

export function buildYearGrid(
  summary: EmployeeTimeYearSummary,
  absenceByDay: Map<string, AbsenceDayMarker[]>,
  calendarByDay: Map<string, EmployeeCalendarDay>,
  year: number,
  heroOverlay?: {
    hoursByDay: Map<string, number>;
    absenceByDay: Map<string, AbsenceDayMarker[]>;
  },
): YearGridData {
  const hoursByMonthDay = new Map<number, Map<number, number>>();
  const pendingByMonthDay = new Map<number, Map<number, boolean>>();
  const totalByMonth = new Map<number, number>();
  for (const monthData of summary.months) {
    const dayMap = new Map<number, number>();
    const pendingMap = new Map<number, boolean>();
    for (const cell of monthData.days) {
      dayMap.set(cell.day, Number(cell.total_hours || 0));
      if (cell.pending_justification) pendingMap.set(cell.day, true);
    }
    hoursByMonthDay.set(monthData.month, dayMap);
    pendingByMonthDay.set(monthData.month, pendingMap);
    totalByMonth.set(monthData.month, Number(monthData.total_hours || 0));
  }
  const balanceByDay = summary.overtime_balance_by_day ?? null;
  const payoutsByDay = summary.overtime_payouts_by_day ?? null;

  let workDayCount = 0;
  let absenceDayCount = 0;
  let yearHeroTotal = 0;
  let yearPayoutTotal = 0;
  const months: YearMonthGrid[] = [];

  for (let month = 1; month <= 12; month++) {
    const numDays = daysInMonth(year, month);
    // Montag = 0 … Sonntag = 6
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
    const cells: Array<YearGridCell | null> = Array.from({ length: firstWeekday }, () => null);
    let monthHeroTotal = 0;

    for (let day = 1; day <= numDays; day++) {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const hours = hoursByMonthDay.get(month)?.get(day) ?? 0;
      const markers = absenceByDay.get(iso) ?? [];
      const heroHours = heroOverlay?.hoursByDay.get(iso) ?? 0;
      const heroMarkers = heroOverlay?.absenceByDay.get(iso) ?? [];
      const balanceRaw = balanceByDay?.[iso];
      const overtimeBalanceHours =
        balanceRaw != null && Number.isFinite(Number(balanceRaw)) ? Number(balanceRaw) : null;
      const payoutRaw = payoutsByDay?.[iso];
      const payoutHours =
        payoutRaw != null && Number.isFinite(Number(payoutRaw)) ? Number(payoutRaw) : 0;
      if (hours > 0 || heroHours > 0) workDayCount += 1;
      if (markers.length > 0) absenceDayCount += 1;
      monthHeroTotal += heroHours;
      yearPayoutTotal += payoutHours;
      cells.push({
        hours,
        heroHours,
        overtimeBalanceHours,
        payoutHours,
        markers,
        heroMarkers,
        calendarDay: calendarByDay.get(iso) ?? null,
        month,
        day,
        iso,
        isWeekend: isWeekend(year, month, day),
        pendingJustification: Boolean(pendingByMonthDay.get(month)?.get(day)),
      });
    }

    while (cells.length % 7 !== 0) cells.push(null);

    const appTotal = totalByMonth.get(month) ?? 0;
    months.push({
      month,
      total: appTotal + monthHeroTotal,
      appTotal,
      heroTotal: monthHeroTotal,
      cells,
    });
    yearHeroTotal += monthHeroTotal;
  }

  const yearAppTotal = Number(summary.total_hours || 0);

  return {
    months,
    yearTotal: yearAppTotal + yearHeroTotal,
    yearAppTotal,
    yearHeroTotal,
    yearPayoutTotal,
    workDayCount,
    absenceDayCount,
  };
}
